// ============================================================
// AI Rådgiver — «Hva skjer hvis …»
// ============================================================
// Scenarioene er hypotetiske beregninger, ikke råd og ikke handlinger. De regner om
// dagens kontantstrømkurve under en gitt forutsetning, slik at brukeren ser utslaget
// på kurven i stedet for bare å lese en påstand.
//
// Ingen scenario bokfører, sender eller lagrer noe. Alt er ren regning på tall som
// allerede ligger i rådgivermodellen.
//
// Flere av scenarioene er flerlinjede: en bedrift ansetter sjelden «én person» og
// investerer sjelden i én ting til én pris. Hver linje regnes for seg og legges
// sammen i den samme kontantstrømmen, slik at ulike lønninger, startdatoer og
// finansieringsformer kan stå side om side.
const PlummeAiAdvisorScenarios = (() => {
  const num = (value, fallback = 0) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };
  const safeArray = (value) => (Array.isArray(value) ? value : []);
  // Product estimates based on the Norwegian chart of accounts, not statutory classifications.
  // Mixed accounts remain visible so the user can exclude them from the scenario.
  const FIXED_COST_ACCOUNT_CODES = new Set([
    '6300', '6340', '6395', '6400', '6440', '6540', '6700', '6701',
    '6840', '6995', '7040', '7400', '7410', '7500', '7565', '7600',
  ]);

  const buildFixedCostBasis = ({ country, year, currentYear, currentMonth, currentLines = [], previousLines = [] } = {}) => {
    if (country !== 'NO' || num(year) !== num(currentYear)) return null;
    const priorHasData = safeArray(previousLines).some((line) => Math.abs(num(line.amount)) > 0);
    const months = priorHasData ? 12 : Math.max(1, Math.min(12, Math.trunc(num(currentMonth)) + 1));
    const byAccount = new Map();
    [...safeArray(previousLines), ...safeArray(currentLines)].forEach((line) => {
      const code = String(line.nr || '').trim();
      const amount = num(line.amount);
      if (!FIXED_COST_ACCOUNT_CODES.has(code) || !amount) return;
      const current = byAccount.get(code) || { code, name: String(line.name || code), total: 0 };
      current.total += amount;
      byAccount.set(code, current);
    });
    const accounts = [...byAccount.values()]
      .map(({ code, name, total }) => ({ code, name, monthly: Math.round(Math.max(0, total) / months) }))
      .filter((item) => item.monthly > 0).sort((a, b) => b.monthly - a.monthly);
    const monthlyTotal = accounts.reduce((sum, item) => sum + item.monthly, 0);
    return monthlyTotal > 0 ? { accounts, monthlyTotal, months } : null;
  };

  // Måneden et scenario starter i, målt som indeks i årets prognose. Datoer utenfor
  // året klemmes inn, ellers ville et scenario med startdato neste år ikke vises.
  const monthIndexFor = (dateString, year) => {
    const text = String(dateString || '');
    const civil = /^(\d{4})-(\d{2})-(\d{2})/.exec(text);
    const date = civil
      ? new Date(Number(civil[1]), Number(civil[2]) - 1, Number(civil[3]))
      : new Date(text);
    if (Number.isNaN(date.getTime())) return 0;
    if (date.getFullYear() < year) return 0;
    if (date.getFullYear() > year) return 11;
    return date.getMonth();
  };

  // Snittet av de månedene som faktisk har bokført inntekt. Å dele årsinntekten på 12
  // ville gitt et kunstig lavt månedstall i et år som ikke er ferdig.
  const monthlyIncomeFrom = (forecast) => {
    const active = safeArray(forecast).filter((point) => num(point.inn) > 0);
    if (!active.length) return 0;
    return Math.round(active.reduce((sum, point) => sum + num(point.inn), 0) / active.length);
  };

  // ---------- Effekt → kurve ----------
  // Effekten uttrykkes som `flows`: netto kontantendring per måned. Det er den eneste
  // formen som bærer alle scenarioene samtidig — et serielån har ulikt terminbeløp
  // hver måned, og fem linjer kan starte i fem forskjellige måneder.
  // `monthlyDelta`/`oneOff` beholdes som en enklere skrivemåte for én løpende endring.
  const flowsFrom = (effect, length, startIndex = 0) => {
    if (!effect) return null;
    const offset = Math.max(0, Math.trunc(num(startIndex)));
    const size = Math.max(0, Math.trunc(num(length))) + offset;
    const flows = new Array(size).fill(0);
    safeArray(effect.flows).forEach((value, index) => {
      if (index < size) flows[index] += num(value);
    });
    const monthly = num(effect.monthlyDelta);
    const fromIndex = Math.max(0, Math.trunc(num(effect.fromIndex)));
    if (monthly) for (let index = fromIndex; index < size; index += 1) flows[index] += monthly;
    const oneOffAmount = num(effect.oneOff?.amount);
    const oneOffIndex = Math.max(0, Math.trunc(num(effect.oneOff?.index, fromIndex)));
    if (oneOffAmount && oneOffIndex < size) flows[oneOffIndex] += oneOffAmount;
    return flows;
  };

  // Flere scenarioer kan være aktive samtidig. De legges sammen på flow-nivå, ikke på
  // ferdige kurver: to kurver som hver er gulvet på 0 kan ikke summeres uten å finne
  // opp penger som aldri fantes.
  const combineEffects = (effects) => {
    const list = safeArray(effects).filter(Boolean);
    if (!list.length) return null;
    const length = list.reduce((max, effect) => Math.max(max, safeArray(effect.flows).length), 0);
    const flows = new Array(length).fill(0);
    list.forEach((effect) => safeArray(effect.flows).forEach((value, index) => { flows[index] += num(value); }));
    return { flows };
  };

  // Negative balances retain the funding shortfall. Inherited certainty means
  // a scenario never makes the underlying figures more reliable.
  //
  // `startIndex` er månedsnummeret den viste kurven begynner på. Flowene er alltid
  // indeksert på kalendermåned, så et vindu som starter i juli må både treffe riktig
  // søyle og bære med seg det som allerede har skjedd før vinduet.
  const applyEffectToForecast = ({ forecast, effect, startIndex = 0 }) => {
    const points = safeArray(forecast);
    const offset = Math.max(0, Math.trunc(num(startIndex)));
    const flows = flowsFrom(effect, points.length, offset);
    if (!flows || !flows.some((value) => value)) return null;
    let running = flows.slice(0, offset).reduce((sum, value) => sum + value, 0);
    return points.map((point, index) => {
      running += flows[offset + index];
      return { ...point, balance: Math.round(num(point.balance) + running) };
    });
  };

  const compareForecasts = ({ base = [], after, buffer = 0 } = {}) => {
    const before = safeArray(base);
    const changed = safeArray(after);
    if (!before.length || changed.length !== before.length) return null;
    const balances = (points) => points.map((point) => num(point.balance));
    const baseBalances = balances(before);
    const afterBalances = balances(changed);
    const limit = num(buffer);
    return {
      baseEnd: baseBalances.at(-1),
      afterEnd: afterBalances.at(-1),
      delta: afterBalances.at(-1) - baseBalances.at(-1),
      baseLow: Math.min(...baseBalances),
      afterLow: Math.min(...afterBalances),
      baseBelowBuffer: limit > 0 ? baseBalances.filter((value) => value < limit).length : null,
      afterBelowBuffer: limit > 0 ? afterBalances.filter((value) => value < limit).length : null,
    };
  };

  // ---------- Scenariodefinisjoner ----------
  // Et scenario tas bare med når datagrunnlaget faktisk bærer det: uten bokført
  // omsetning kan vi ikke regne på prisøkning eller omsetningsfall, og uten kjent
  // kundekonsentrasjon vet vi ikke hva største kunde er verdt.
  //
  // `rowFields` er linjefeltene i et flerlinjet scenario. `loanFields` er lånevilkår
  // som ligger i egen modal — de gjelder bare når linjen finansieres med lån, og ville
  // ellers dyttet fire ekstra felter inn i et skjema de fleste ikke trenger.
  const buildScenarios = ({ forecast = [], income = 0, concentration = null, employerRate = 14.1, holidayRate = 10.2, fixedCostBasis = null, year } = {}) => {
    const monthlyIncome = monthlyIncomeFrom(forecast);
    const currentYear = new Date().getFullYear();
    const startYear = num(year, currentYear);
    const defaultDate = `${startYear}-${String(Math.min(11, new Date().getMonth() + 1) + 1).padStart(2, '0')}-01`;
    const list = [];
    list.push({
      id: 'hire',
      icon: 'users',
      editable: true,
      repeatable: true,
      rowFields: [
        { key: 'startDate', kind: 'date', value: defaultDate },
        { key: 'monthlySalary', kind: 'number', value: 38000 },
        // Standarden er foretakets egen arbeidsgiveravgift pluss feriepengesats, ikke
        // et påfunnet tall.
        { key: 'socialPct', kind: 'number', value: Math.round((num(employerRate, 14.1) + num(holidayRate, 10.2)) * 10) / 10 },
        { key: 'extraRevenue', kind: 'number', value: 0 },
      ],
    });
    list.push({
      id: 'invest',
      icon: 'briefcase',
      editable: true,
      repeatable: true,
      rowFields: [
        { key: 'amount', kind: 'number', value: 95000 },
        { key: 'payDate', kind: 'date', value: defaultDate },
        { key: 'financing', kind: 'select', value: 'cash', options: ['cash', 'loan'] },
      ],
      loanFields: [
        { key: 'loanType', kind: 'select', value: 'annuity', options: ['annuity', 'serial'] },
        { key: 'interestPct', kind: 'number', value: 7.5, step: 0.1, min: 0, max: 25 },
        { key: 'equityPct', kind: 'slider', value: 30, min: 0, max: 100, step: 1 },
        { key: 'termMonths', kind: 'slider', value: 48, min: 6, max: 120, step: 1 },
      ],
    });
    list.push({
      id: 'fixedCost',
      icon: 'wallet',
      editable: true,
      repeatable: true,
      rowFields: [
        { key: 'costLabel', kind: 'text', value: '' },
        { key: 'monthlyAmount', kind: 'number', value: 5000 },
        { key: 'startDate', kind: 'date', value: defaultDate },
      ],
    });
    if (fixedCostBasis?.monthlyTotal > 0) list.push({
      id: 'reduceFixedCosts', icon: 'trendDown', editable: true, fixedCostBasis,
      slider: { key: 'pct', min: 0, max: 100, step: 1, value: 10 },
    });
    if (monthlyIncome > 0) {
      // Prisøkningen får gå helt til 200 %: spørsmålet «hva om jeg tredobler prisen»
      // er like legitimt som «hva om jeg tar 5 % mer». Fallet stopper på 100 % —
      // omsetningen kan ikke falle mer enn den er.
      list.push({ id: 'priceUp', icon: 'trendUp', editable: true, pct: 5, slider: { key: 'pct', min: 0, max: 200, step: 1, value: 5 } });
      list.push({ id: 'revenueDown', icon: 'trendDown', editable: true, pct: 20, slider: { key: 'pct', min: 0, max: 100, step: 1, value: 20 } });
    }
    if (concentration != null && monthlyIncome > 0) {
      list.push({ id: 'loseTopCustomer', icon: 'alertTri', editable: false, pct: Math.round(num(concentration)) });
    }
    return { scenarios: list, monthlyIncome, income: num(income) };
  };

  // ---------- Linjer ----------
  // Standardlinjen er feltverdiene fra definisjonen. Et scenario uten lagrede linjer
  // har alltid nøyaktig én linje — brukeren skal aldri møte et tomt skjema.
  const defaultRow = (scenario) => {
    const row = {};
    safeArray(scenario?.rowFields).forEach((field) => { row[field.key] = field.value; });
    safeArray(scenario?.loanFields).forEach((field) => { row[field.key] = field.value; });
    return row;
  };
  const rowsOf = (scenario, inputs = {}) => {
    const base = defaultRow(scenario);
    const rows = safeArray(inputs.rows).filter((row) => row && typeof row === 'object');
    if (!rows.length) return [base];
    return rows.map((row) => ({ ...base, ...row }));
  };

  // ---------- Beregning ----------
  // Returnerer effekten på kurven pluss nøkkel/variabler for forklaringsteksten.
  // Teksten oversettes i UI — beregningen eier ikke språk.
  const addRecurring = (flows, fromIndex, amount) => {
    for (let index = Math.max(0, fromIndex); index < flows.length; index += 1) flows[index] += amount;
  };
  const addOnce = (flows, index, amount) => {
    if (index >= 0 && index < flows.length) flows[index] += amount;
  };
  const earliest = (dates) => dates.filter(Boolean).sort()[0] || '';

  const computeHire = ({ rows, flows, year }) => {
    let cost = 0;
    let extra = 0;
    rows.forEach((row) => {
      const salary = Math.max(0, num(row.monthlySalary));
      const socialPct = Math.max(0, num(row.socialPct));
      const rowCost = Math.round(salary * (1 + socialPct / 100));
      const rowExtra = num(row.extraRevenue);
      cost += rowCost;
      extra += rowExtra;
      addRecurring(flows, monthIndexFor(row.startDate, year), rowExtra - rowCost);
    });
    const net = extra - cost;
    return {
      summaryKey: rows.length > 1 ? 'hireMany' : 'hire',
      summaryVars: { count: rows.length, cost, net: Math.abs(net), extra, date: earliest(rows.map((row) => row.startDate)) },
      direction: net >= 0 ? 'up' : 'down',
    };
  };

  const computeInvest = ({ rows, flows, year }) => {
    const loan = globalThis.PlummeAiAdvisorLoan;
    let amount = 0;
    let upfront = 0;
    let monthly = 0;
    let interest = 0;
    let termMonths = 0;
    let hasLoan = false;
    rows.forEach((row) => {
      const rowAmount = Math.max(0, num(row.amount));
      const payIndex = monthIndexFor(row.payDate, year);
      amount += rowAmount;
      if (row.financing !== 'loan' || !loan) {
        upfront += rowAmount;
        addOnce(flows, payIndex, -rowAmount);
        return;
      }
      hasLoan = true;
      const equityPct = Math.min(100, Math.max(0, num(row.equityPct, 30)));
      const equity = Math.round(rowAmount * equityPct / 100);
      const financed = Math.max(0, rowAmount - equity);
      const rowTerm = Math.max(1, Math.trunc(num(row.termMonths, 48)));
      const schedule = loan.buildSchedule({
        principal: financed,
        annualRatePct: num(row.interestPct, 0),
        termMonths: rowTerm,
        type: row.loanType,
      });
      const totals = loan.summarize(schedule);
      upfront += equity;
      monthly += totals.firstPayment;
      interest += totals.totalInterest;
      termMonths = Math.max(termMonths, rowTerm);
      addOnce(flows, payIndex, -equity);
      // Første termin forfaller måneden etter kjøpet — lånet betales ikke ned samme
      // måned som det tas opp.
      schedule.forEach((entry, offset) => addOnce(flows, payIndex + 1 + offset, -entry.payment));
    });
    return {
      summaryKey: hasLoan ? 'investLoan' : 'investCash',
      summaryVars: { count: rows.length, amount, equity: upfront, monthly, interest, termMonths },
      direction: 'down',
    };
  };

  const computeFixedCost = ({ rows, flows, year }) => {
    let cost = 0;
    rows.forEach((row) => {
      const rowCost = Math.max(0, num(row.monthlyAmount));
      cost += rowCost;
      addRecurring(flows, monthIndexFor(row.startDate, year), -rowCost);
    });
    return {
      summaryKey: rows.length > 1 ? 'fixedCostMany' : 'fixedCost',
      summaryVars: { count: rows.length, cost, yearly: cost * 12, date: earliest(rows.map((row) => row.startDate)) },
      direction: 'down',
    };
  };

  // Prosentscenarioene styres av én slider. Verdien leses fra brukerens input, ellers
  // fra scenarioets egen standard — den samme verdien som står i knappeteksten.
  const pctOf = (scenario, inputs) => {
    const raw = inputs?.pct !== undefined ? inputs.pct : (scenario.slider?.value ?? scenario.pct);
    const value = Math.max(0, num(raw));
    const max = num(scenario.slider?.max, 200);
    return Math.min(max, value);
  };

  const computeScenario = ({ scenario, inputs = {}, context = {} } = {}) => {
    if (!scenario) return null;
    const monthlyIncome = num(context.monthlyIncome);
    const year = num(context.year, new Date().getFullYear());
    const months = Math.max(1, Math.trunc(num(context.months, 12)));
    const flows = new Array(months).fill(0);
    const rows = rowsOf(scenario, inputs);
    let result = null;
    if (scenario.id === 'hire') result = computeHire({ rows, flows, year });
    else if (scenario.id === 'invest') result = computeInvest({ rows, flows, year });
    else if (scenario.id === 'fixedCost') result = computeFixedCost({ rows, flows, year });
    else if (scenario.id === 'reduceFixedCosts') {
      const pct = pctOf(scenario, inputs);
      const excluded = new Set(safeArray(inputs.excludedAccountCodes).map(String));
      const base = safeArray(scenario.fixedCostBasis?.accounts)
        .filter((account) => !excluded.has(account.code))
        .reduce((sum, account) => sum + Math.max(0, num(account.monthly)), 0);
      const delta = Math.round(base * pct / 100);
      addRecurring(flows, Math.max(0, Math.trunc(num(context.startIndex))), delta);
      result = {
        summaryKey: 'reduceFixedCosts',
        summaryVars: { pct, base, delta },
        direction: 'up',
      };
    }
    else if (scenario.id === 'priceUp' || scenario.id === 'revenueDown' || scenario.id === 'loseTopCustomer') {
      const pct = pctOf(scenario, inputs);
      const delta = Math.round(monthlyIncome * pct / 100);
      const up = scenario.id === 'priceUp';
      addRecurring(flows, 0, up ? delta : -delta);
      result = { summaryKey: scenario.id, summaryVars: { pct, delta }, direction: up ? 'up' : 'down' };
    }
    if (!result) return null;
    return { ...result, effect: { flows: flows.map((value) => Math.round(value)) } };
  };

  // Tonen sier hva utslaget betyr for bufferen, ikke om scenarioet er «bra». En
  // prisøkning som løfter kurven er grønn; et fall under buffer er rødt.
  const toneFor = ({ forecast, buffer }) => {
    const points = safeArray(forecast);
    if (!points.length) return 'amber';
    const lowest = points.reduce((min, point) => (num(point.balance) < num(min.balance) ? point : min), points[0]);
    if (!num(buffer)) return 'amber';
    if (num(lowest.balance) < num(buffer) * 0.75) return 'red';
    if (num(lowest.balance) < num(buffer)) return 'amber';
    return 'green';
  };

  return { buildFixedCostBasis, buildScenarios, computeScenario, combineEffects, applyEffectToForecast, compareForecasts, toneFor, monthlyIncomeFrom, monthIndexFor, defaultRow, rowsOf, pctOf };
})();

Object.assign(globalThis, { PlummeAiAdvisorScenarios });
